/**
 * Chat d'équipe en temps réel, intégré dans la page "Mon équipe" (sans
 * changer de page, contrairement au chat de la page "Discussions").
 *
 * Réutilise les briques de rendu de messages de la page Chat (bulle, quote,
 * réactions, invitations, partages, saisie + menu « + ») branchées sur le
 * groupe de discussion d'équipe (`GET /api/conversations/team-group`).
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { PlayerAvatar } from '../PlayerAvatar';
import ClickablePseudo from '../ClickablePseudo';
import ChatMessageContent from '../chat/ChatMessageContent';
import ChatMessageShell from '../chat/ChatMessageShell';
import MessageReactions from '../chat/MessageReactions';
import MessageQuoteBlock from '../chat/MessageQuoteBlock';
import ChatInputComposer from '../chat/ChatInputComposer';
import GameInviteMessage from '../chat/GameInviteMessage';
import LibraryShareMessage from '../chat/LibraryShareMessage';
import CiaAgendaInviteMessage from '../chat/CiaAgendaInviteMessage';
import GameInviteOfflineConfirm from '../chat/GameInviteOfflineConfirm';
import GameInviteExistingConfirm from '../chat/GameInviteExistingConfirm';
import { MessageStatusIcon } from '../Icons';
import { useApp } from '../../contexts/AppContext';
import { insertAtCursor } from '../../utils/emoticons';
import { formatChatMessagePreview, parseChatDeepLinks } from '../../utils/chatDeepLinks';
import { isBotPseudo } from '../../utils/pseudo';
import type {
  ReplyTarget,
  MessageQuote,
  MessageReaction,
  GameInvitePayload,
  LibrarySharePayload,
  CiaAgendaInvitePayload,
} from '../../types/chat';
import { BACKEND_URL } from '../../config';
import '../../styles/chat.css';
import type { Session } from '../../types/app';

interface TeamMessage {
  id: string;
  conversationId: string;
  author: string;
  text: string;
  createdAt: string;
  status: 'sent' | 'delivered' | 'read';
  quote?: MessageQuote;
  reactions?: MessageReaction[];
  messageType?: 'text' | 'game_invite' | 'library_share' | 'cia_agenda_invite';
  gameInvite?: GameInvitePayload;
  libraryShare?: LibrarySharePayload;
  ciaAgendaInvite?: CiaAgendaInvitePayload;
}

interface TeamChatPanelProps {
  session: Session;
  /** Appelé après marquage lu (pour synchroniser la bulle du hub). */
  onMarkedRead?: () => void;
}

function formatTime(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function getDateKey(v: string): string {
  const d = new Date(v);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

const WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

function formatDateSeparator(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today.getTime() - day.getTime()) / 86400000);
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return 'Hier';
  if (diffDays > 1 && diffDays < 7) return WEEKDAYS[d.getDay()];
  return d.toLocaleDateString('fr-FR');
}

function getConnectedTeammates(
  participants: string[],
  currentUserPseudo: string,
  connectedUsers: { pseudo: string }[],
): string[] {
  const connectedPseudos = new Set(connectedUsers.map((u) => u.pseudo.toLowerCase()));
  return participants.filter(
    (p) =>
      p.toLowerCase() !== currentUserPseudo.toLowerCase()
      && !isBotPseudo(p)
      && connectedPseudos.has(p.toLowerCase()),
  );
}

function formatOtherParticipantNames(participants: string[], currentUserPseudo: string): string {
  const others = participants
    .filter((p) => p.toLowerCase() !== currentUserPseudo.toLowerCase() && !isBotPseudo(p))
    .sort((a, b) => a.localeCompare(b, 'fr'));
  if (others.length === 0) return '';
  if (others.length === 1) return others[0];
  const last = others.pop()!;
  return `${others.join(', ')} et ${last}`;
}

export default function TeamChatPanel({ session, onMarkedRead }: TeamChatPanelProps) {
  const {
    navigateToLibrary,
    navigateToDiffere,
    showToast,
    setActiveTab,
    connectedUsers,
    gameInfo,
  } = useApp();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [participants, setParticipants] = useState<string[]>([]);
  const [messages, setMessages] = useState<TeamMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [replyingTo, setReplyingTo] = useState<ReplyTarget | null>(null);
  const [sending, setSending] = useState(false);
  const [showOfflineInviteConfirm, setShowOfflineInviteConfirm] = useState(false);
  const [showExistingInviteConfirm, setShowExistingInviteConfirm] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const onMarkedReadRef = useRef(onMarkedRead);
  onMarkedReadRef.current = onMarkedRead;

  const scrollToEnd = useCallback((behavior: ScrollBehavior = 'smooth') => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior }), 50);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${BACKEND_URL}/api/conversations/team-group?pseudo=${encodeURIComponent(session.pseudo)}`)
      .then((r) => r.json())
      .then((data: {
        teamGroup?: { id: string; participants?: string[]; unreadCount?: number } | null;
      }) => {
        if (cancelled) return;
        if (!data.teamGroup) {
          setError("Impossible de retrouver la discussion d'équipe");
          setLoading(false);
          return;
        }
        setConversationId(data.teamGroup.id);
        setParticipants(data.teamGroup.participants || []);
      })
      .catch(() => {
        if (!cancelled) { setError('Impossible de charger le chat'); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [session.pseudo]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/conversations/${conversationId}/messages?limit=50`)
      .then((r) => r.json())
      .then(async (data: { messages?: TeamMessage[] }) => {
        if (cancelled) return;
        setMessages(data.messages || []);

        try {
          await fetch(`${BACKEND_URL}/api/conversations/${conversationId}/read`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pseudo: session.pseudo }),
          });
        } catch { /* ignore */ }
        if (!cancelled) onMarkedReadRef.current?.();
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger les messages'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [conversationId, session.pseudo]);

  useEffect(() => {
    const socket = io(BACKEND_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('connect', () => {
      socket.emit('join_global', { pseudo: session.pseudo, compet: session.compet });
      if (conversationId) socket.emit('join_conversation', conversationId);
    });
    return () => { socket.disconnect(); socketRef.current = null; };
  }, [session.pseudo, session.compet, conversationId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !conversationId) return;
    socket.emit('join_conversation', conversationId);

    const upsertMessage = (message: TeamMessage) => {
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      scrollToEnd();
    };

    const onMessage = (message: TeamMessage) => {
      if (message.conversationId !== conversationId) return;
      upsertMessage(message);
      if (message.author.toLowerCase() !== session.pseudo.toLowerCase()) {
        fetch(`${BACKEND_URL}/api/conversations/${conversationId}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pseudo: session.pseudo }),
        }).catch(() => {});
        onMarkedReadRef.current?.();
      }
    };

    const onReaction = (data: { conversationId?: string; messageId: string; reactions: MessageReaction[] }) => {
      if (data.conversationId !== conversationId) return;
      setMessages((prev) => prev.map((m) => (m.id === data.messageId ? { ...m, reactions: data.reactions } : m)));
    };

    const onGameInviteUpdated = (data: {
      conversationId: string;
      message: {
        id: string;
        author: string;
        text: string;
        createdAt: string;
        status: string;
        messageType: 'game_invite';
        gameInvite: GameInvitePayload;
      };
    }) => {
      if (data.conversationId !== conversationId) return;
      setMessages((prev) => prev.map((m) =>
        m.id === data.message.id
          ? {
              ...m,
              text: data.message.text,
              gameInvite: data.message.gameInvite,
              messageType: 'game_invite',
            }
          : m,
      ));
    };

    const onLibraryShareUpdated = (data: {
      conversationId: string;
      message: {
        id: string;
        text: string;
        messageType: 'library_share';
        libraryShare: LibrarySharePayload;
      };
    }) => {
      if (data.conversationId !== conversationId) return;
      setMessages((prev) => prev.map((m) =>
        m.id === data.message.id
          ? {
              ...m,
              text: data.message.text,
              libraryShare: data.message.libraryShare,
              messageType: 'library_share',
            }
          : m,
      ));
    };

    const onCiaAgendaInviteUpdated = (data: {
      conversationId: string;
      message: {
        id: string;
        text: string;
        messageType: 'cia_agenda_invite';
        ciaAgendaInvite: CiaAgendaInvitePayload;
      };
    }) => {
      if (data.conversationId !== conversationId) return;
      setMessages((prev) => prev.map((m) =>
        m.id === data.message.id
          ? {
              ...m,
              text: data.message.text,
              ciaAgendaInvite: data.message.ciaAgendaInvite,
              messageType: 'cia_agenda_invite',
            }
          : m,
      ));
    };

    socket.on('direct_message', onMessage);
    socket.on('message_reaction', onReaction);
    socket.on('game_invite_updated', onGameInviteUpdated);
    socket.on('library_share_updated', onLibraryShareUpdated);
    socket.on('cia_agenda_invite_updated', onCiaAgendaInviteUpdated);
    return () => {
      socket.off('direct_message', onMessage);
      socket.off('message_reaction', onReaction);
      socket.off('game_invite_updated', onGameInviteUpdated);
      socket.off('library_share_updated', onLibraryShareUpdated);
      socket.off('cia_agenda_invite_updated', onCiaAgendaInviteUpdated);
      socket.emit('leave_conversation', conversationId);
    };
  }, [conversationId, session.pseudo, scrollToEnd]);

  useEffect(() => {
    if (!loading) messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [loading, conversationId]);

  const send = useCallback(async () => {
    if (!conversationId || !inputText.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pseudo: session.pseudo,
          text: inputText.trim(),
          ...(replyingTo ? { replyToId: replyingTo.id } : {}),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
        setInputText('');
        setReplyingTo(null);
        inputRef.current?.focus();
        scrollToEnd();
      }
    } catch {
      // Le texte reste dans le champ pour permettre de réessayer
    } finally {
      setSending(false);
    }
  }, [conversationId, inputText, replyingTo, sending, session.pseudo, scrollToEnd]);

  const toggleReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!conversationId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/conversations/${conversationId}/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: session.pseudo, emoji }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions: data.reactions } : m)));
      }
    } catch { /* ignore */ }
  }, [conversationId, session.pseudo]);

  const insertEmoji = useCallback((emoji: string) => {
    const { next, caret } = insertAtCursor(inputText, emoji, inputRef.current);
    setInputText(next);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(caret, caret);
    });
  }, [inputText]);

  const navigateToDraft = useCallback((draftId: string, gameMode?: string) => {
    sessionStorage.setItem('wt_active_draft', draftId);
    setActiveTab('tables');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('join-draft-from-invitation', {
        detail: { draftId, gameMode },
      }));
    }, 100);
  }, [setActiveTab]);

  const pendingGameInviteMessage = useMemo(() => (
    messages
      .filter((m) =>
        m.messageType === 'game_invite'
        && m.gameInvite?.status === 'pending'
        && m.author.toLowerCase() === session.pseudo.toLowerCase(),
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null
  ), [messages, session.pseudo]);

  const gameInviteMeta = useMemo(() => {
    const hasActiveDraft = !!sessionStorage.getItem('wt_active_draft');
    if (gameInfo?.neo && gameInfo.neoTableId) {
      return {
        title: 'Inviter à ma table',
        subtitle: 'Invitez le groupe à rejoindre une nouvelle table',
      };
    }
    if (hasActiveDraft) {
      return {
        title: 'Inviter à ma table',
        subtitle: 'Invitez le groupe à rejoindre une nouvelle table',
      };
    }
    if (gameInfo) {
      return {
        title: 'Inviter à jouer',
        subtitle: 'Vous êtes en partie — une nouvelle table sera proposée',
      };
    }
    return {
      title: 'Inviter à jouer',
      subtitle: 'Proposez au groupe de créer une nouvelle table',
    };
  }, [gameInfo]);

  const offlineInviteParticipantNames = useMemo(
    () => formatOtherParticipantNames(participants, session.pseudo),
    [participants, session.pseudo],
  );

  const sendGameInvite = useCallback(async () => {
    if (!conversationId) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/conversations/${conversationId}/game-invites`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pseudo: session.pseudo,
            compet: session.compet,
          }),
        },
      );
      if (!response.ok) {
        showToast({ message: "Impossible d'envoyer l'invitation", type: 'info', duration: 4500 });
        return;
      }
      const data = await response.json();
      const now = new Date().toISOString();
      setMessages((prev) => {
        if (prev.some((m) => m.id === data.messageId)) return prev;
        return [...prev, {
          id: data.messageId,
          conversationId,
          author: session.pseudo,
          text: data.gameInvite ? '🎮 Invitation à jouer' : 'Invitation à jouer',
          createdAt: now,
          status: 'sent' as const,
          messageType: 'game_invite' as const,
          gameInvite: data.gameInvite,
        }];
      });
      showToast({ message: 'Invitation publiée dans la conversation', type: 'game', duration: 4000 });
      if (data.gameInvite?.draftId) {
        sessionStorage.setItem('wt_active_draft', data.gameInvite.draftId);
      }
      scrollToEnd();
    } catch {
      showToast({ message: "Impossible d'envoyer l'invitation", type: 'info', duration: 4500 });
    }
  }, [conversationId, session.pseudo, session.compet, showToast, scrollToEnd]);

  const requestGameInviteAfterChecks = useCallback(() => {
    if (!conversationId) return;
    const connected = getConnectedTeammates(participants, session.pseudo, connectedUsers);
    if (connected.length === 0) {
      setShowOfflineInviteConfirm(true);
      return;
    }
    void sendGameInvite();
  }, [conversationId, participants, session.pseudo, connectedUsers, sendGameInvite]);

  const handleAttachPoll = useCallback(() => {
    showToast({
      message: 'Les sondages dans les discussions arrivent bientôt',
      type: 'info',
      duration: 4000,
    });
  }, [showToast]);

  const handleAttachGameInvite = useCallback(() => {
    if (pendingGameInviteMessage) {
      setShowExistingInviteConfirm(true);
      return;
    }
    requestGameInviteAfterChecks();
  }, [pendingGameInviteMessage, requestGameInviteAfterChecks]);

  const cancelGameInvite = useCallback(async (messageId: string): Promise<boolean> => {
    if (!conversationId) return false;
    const response = await fetch(
      `${BACKEND_URL}/api/conversations/${conversationId}/messages/${messageId}/game-invite/cancel`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: session.pseudo }),
      },
    );
    if (!response.ok) {
      showToast({ message: "Impossible d'annuler l'invitation", type: 'info' });
      return false;
    }
    const data = await response.json();
    setMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, gameInvite: data.gameInvite } : m,
    ));
    return true;
  }, [conversationId, session.pseudo, showToast]);

  const respondToGameInvite = useCallback(async (messageId: string, accepted: boolean) => {
    if (!conversationId) return;
    const response = await fetch(
      `${BACKEND_URL}/api/conversations/${conversationId}/messages/${messageId}/game-invite/respond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: session.pseudo, accepted }),
      },
    );
    if (!response.ok) {
      showToast({ message: "Impossible de répondre à l'invitation", type: 'info' });
      return;
    }
    const data = await response.json();
    setMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, gameInvite: data.gameInvite } : m,
    ));
    if (accepted && data.gameInvite?.draftId) {
      navigateToDraft(data.gameInvite.draftId, data.gameInvite.config?.gameMode);
    }
  }, [conversationId, session.pseudo, showToast, navigateToDraft]);

  const handleReplaceExistingGameInvite = useCallback(async () => {
    setShowExistingInviteConfirm(false);
    if (!pendingGameInviteMessage) return;
    const cancelled = await cancelGameInvite(pendingGameInviteMessage.id);
    if (cancelled) requestGameInviteAfterChecks();
  }, [pendingGameInviteMessage, cancelGameInvite, requestGameInviteAfterChecks]);

  const cancelLibraryShare = useCallback(async (sharedGameId: number): Promise<boolean> => {
    if (!window.confirm("Annuler ce partage ? Les destinataires n'y auront plus accès.")) {
      return false;
    }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${sharedGameId}?pseudo=${encodeURIComponent(session.pseudo)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        showToast({ message: "Impossible d'annuler le partage", type: 'info' });
        return false;
      }
      return true;
    } catch {
      showToast({ message: "Impossible d'annuler le partage", type: 'info' });
      return false;
    }
  }, [session.pseudo, showToast]);

  const respondToCiaAgendaInvite = useCallback(async (invite: CiaAgendaInvitePayload, accepted: boolean) => {
    const action = accepted ? 'accept' : 'refuse';
    try {
      const res = await fetch(`${BACKEND_URL}/api/competition/cia/agenda/${invite.agendaId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Pseudo': session.pseudo,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        showToast({ message: json.error || 'Impossible de répondre', type: 'info' });
        return;
      }
      showToast({
        message: accepted ? 'Rendez-vous CIA accepté' : 'Proposition CIA refusée',
        type: 'info',
      });
    } catch {
      showToast({ message: 'Impossible de répondre à la proposition CIA', type: 'info' });
    }
  }, [session.pseudo, showToast]);

  const cancelCiaAgendaInvite = useCallback(async (invite: CiaAgendaInvitePayload) => {
    const isProposer = invite.proposerPseudo.toLowerCase() === session.pseudo.toLowerCase();
    const action = isProposer ? 'cancel' : 'cancel-destinataire';
    try {
      const res = await fetch(`${BACKEND_URL}/api/competition/cia/agenda/${invite.agendaId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Pseudo': session.pseudo,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        showToast({ message: json.error || "Impossible d'annuler", type: 'info' });
      }
    } catch {
      showToast({ message: "Impossible d'annuler la proposition CIA", type: 'info' });
    }
  }, [session.pseudo, showToast]);

  if (loading) return <div className="compet-loading">Chargement du chat…</div>;
  if (error) return <div className="compet-error">{error}</div>;

  return (
    <div className="tm-chat-panel">
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="messages-empty">
            <p>💬</p>
            <p>Démarrez la discussion d&apos;équipe !</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isOwn = msg.author.toLowerCase() === session.pseudo.toLowerCase();
            const isSpecial =
              msg.messageType === 'game_invite'
              || msg.messageType === 'library_share'
              || msg.messageType === 'cia_agenda_invite';
            const showAuthor = !isOwn && (index === 0 || messages[index - 1].author !== msg.author);
            const currentDateKey = getDateKey(msg.createdAt);
            const previousDateKey = index > 0 ? getDateKey(messages[index - 1].createdAt) : null;
            const showDateSeparator = index === 0 || currentDateKey !== previousDateKey;
            const replyTarget: ReplyTarget = {
              id: msg.id,
              author: msg.author,
              text: formatChatMessagePreview(msg.text),
            };
            const deepLinkPresentation = parseChatDeepLinks(msg.text);
            const hasDeepLinkCards = deepLinkPresentation.cards.length > 0;
            const deepLinkCardsOnly = hasDeepLinkCards && !deepLinkPresentation.displayText;

            const content = (
              <div className={`message message-interactive ${isOwn ? 'outgoing' : 'incoming'}`}>
                {!isOwn && showAuthor && <PlayerAvatar pseudo={msg.author} size={32} className="message-avatar" />}
                {!isOwn && !showAuthor && <div className="message-avatar-placeholder" />}
                <div className="message-bubble-wrap">
                  <div className={`message-bubble ${
                    isSpecial || deepLinkCardsOnly ? 'game-invite-bubble' : ''
                  }`}
                  >
                    {showAuthor && !isSpecial && (
                      <div className="message-author">
                        <ClickablePseudo pseudo={msg.author}>{msg.author}</ClickablePseudo>
                      </div>
                    )}
                    {msg.messageType === 'game_invite' && msg.gameInvite ? (
                      <>
                        <GameInviteMessage
                          invite={msg.gameInvite}
                          author={msg.author}
                          currentPseudo={session.pseudo}
                          showProposer
                          showCancelledRecipients
                          onRespond={(accepted) => respondToGameInvite(msg.id, accepted)}
                          onCancel={async () => { await cancelGameInvite(msg.id); }}
                          onAccess={() => navigateToDraft(
                            msg.gameInvite!.draftId,
                            msg.gameInvite!.config.gameMode,
                          )}
                        />
                        <span className="message-meta game-invite-meta">
                          <span className="message-time">{formatTime(msg.createdAt)}</span>
                        </span>
                      </>
                    ) : msg.messageType === 'cia_agenda_invite' && msg.ciaAgendaInvite ? (
                      <>
                        <CiaAgendaInviteMessage
                          invite={msg.ciaAgendaInvite}
                          author={msg.author}
                          currentPseudo={session.pseudo}
                          onRespond={(accepted) => respondToCiaAgendaInvite(msg.ciaAgendaInvite!, accepted)}
                          onCancel={async () => { await cancelCiaAgendaInvite(msg.ciaAgendaInvite!); }}
                        />
                        <span className="message-meta game-invite-meta">
                          <span className="message-time">{formatTime(msg.createdAt)}</span>
                        </span>
                      </>
                    ) : msg.messageType === 'library_share' && msg.libraryShare ? (
                      <>
                        <LibraryShareMessage
                          share={msg.libraryShare}
                          author={msg.author}
                          showProposer
                          canCancel={
                            !msg.libraryShare.cancelledAt
                            && msg.author.toLowerCase() === session.pseudo.toLowerCase()
                          }
                          onCancel={async () => { await cancelLibraryShare(msg.libraryShare!.sharedGameId); }}
                          onOpen={() => {
                            const isSharer = msg.author.toLowerCase() === session.pseudo.toLowerCase();
                            navigateToLibrary({
                              section: isSharer ? 'my_shares' : 'shared_private',
                              sharedGameId: msg.libraryShare!.sharedGameId,
                              mode: msg.libraryShare!.isPoll ? 'poll' : 'replay',
                            });
                          }}
                        />
                        <span className="message-meta game-invite-meta">
                          <span className="message-time">{formatTime(msg.createdAt)}</span>
                        </span>
                      </>
                    ) : (
                      <>
                        {msg.quote && (
                          <MessageQuoteBlock
                            quote={msg.quote}
                            isOutgoing={isOwn}
                          />
                        )}
                        <ChatMessageContent
                          text={msg.text}
                          onOpenLibrary={navigateToLibrary}
                          onOpenDiffere={navigateToDiffere}
                          meta={(
                            <>
                              {isOwn && (
                                <span className="message-status"><MessageStatusIcon status={msg.status} size={11} /></span>
                              )}
                              <span className="message-time">{formatTime(msg.createdAt)}</span>
                            </>
                          )}
                        />
                      </>
                    )}
                  </div>
                  {!isSpecial && (
                    <MessageReactions
                      reactions={msg.reactions ?? []}
                      currentPseudo={session.pseudo}
                      onReact={(emoji) => toggleReaction(msg.id, emoji)}
                    />
                  )}
                </div>
              </div>
            );

            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && (
                  <div className="date-separator sticky">
                    <span className="date-separator-text">{formatDateSeparator(msg.createdAt)}</span>
                  </div>
                )}
                {msg.messageType === 'game_invite' ? (
                  content
                ) : (
                  <ChatMessageShell
                    messageId={msg.id}
                    alignRight={isOwn}
                    onReply={setReplyingTo}
                    onReact={(emoji) => toggleReaction(msg.id, emoji)}
                    replyTarget={replyTarget}
                  >
                    {content}
                  </ChatMessageShell>
                )}
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <ChatInputComposer
        value={inputText}
        onChange={setInputText}
        onSend={send}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        placeholder="Écrire à l'équipe…"
        disabled={sending}
        replyingTo={replyingTo}
        onDismissReply={() => setReplyingTo(null)}
        insertEmoji={insertEmoji}
        inputRef={inputRef}
        showAttachMenu
        onPoll={handleAttachPoll}
        onGameInvite={handleAttachGameInvite}
        gameInviteTitle={gameInviteMeta.title}
        gameInviteSubtitle={gameInviteMeta.subtitle}
      />

      {showOfflineInviteConfirm && (
        <GameInviteOfflineConfirm
          participantNames={offlineInviteParticipantNames}
          onConfirm={() => {
            setShowOfflineInviteConfirm(false);
            void sendGameInvite();
          }}
          onCancel={() => setShowOfflineInviteConfirm(false)}
        />
      )}

      {showExistingInviteConfirm && (
        <GameInviteExistingConfirm
          onReplace={() => { void handleReplaceExistingGameInvite(); }}
          onKeep={() => setShowExistingInviteConfirm(false)}
        />
      )}
    </div>
  );
}
